"use client";

import { Trans, useLingui } from "@lingui/react/macro";
import { Button, Input } from "@offstreetparking/design-system/forms";
import { Heading, Text } from "@offstreetparking/design-system/typography";
import { ButtonRow } from "@offstreetparking/portal-flow/components/layouts/ButtonRow";
import { ContentCard } from "@offstreetparking/portal-flow/components/layouts/ContentCard";
import { buildSupportUrl } from "@offstreetparking/portal-flow/utils/support-url";
import { useRouter } from "next/navigation";
import { type FormEvent, use, useState } from "react";
import { CommonLayout } from "~/components/layouts/CommonLayout";
import { useLegacyRedirect } from "./hooks/useLegacyRedirect";
type HomePageProps = {
	params: Promise<{
		locale: string;
	}>;
};
export default function HomePage(props: HomePageProps) {
	const params = use(props.params);
	const { locale } = params;
	const { t } = useLingui();
	const router = useRouter();
	const [locationNumber, setLocationNumber] = useState("");
	useLegacyRedirect();
	const supportUrl = buildSupportUrl({ ticketForm: "general_parker_support" });
	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		const trimmed = locationNumber.trim();
		if (trimmed) {
			router.push(`/${locale}/${encodeURIComponent(trimmed)}`);
		}
	};
	return (
		<CommonLayout
			srOnlyTitle={t`Thanks for Parking with Us`}
			infoSlot={
				<div className="space-y-6">
					<Heading As="h1" className="font-extrabold text-5xl leading-tight">
						<Trans>
							Thanks for <span className="text-primary">Parking with Us</span>
						</Trans>
					</Heading>
					<Text size="md">
						{t`To park, enter the location number and your license plate information. Enjoy your visit!`}
					</Text>
				</div>
			}
		>
			<ContentCard>
				<form onSubmit={handleSubmit} className="flex h-full flex-col p-6 md:p-8">
					<div className="space-y-2">
						<Heading As="h2" kind="h3">
							{t`Where are you parking?`}
						</Heading>
					</div>
					<div className="mt-4">
						<Input
							name="location-number"
							label={t`Location Number`}
							placeholder={t`Enter Location Number`}
							value={locationNumber}
							onChange={(e) => setLocationNumber(e.target.value)}
						/>
					</div>
					<Text size="sm" className="mt-2 text-neutral-faded">
						{t`Check the parking lot signage for your location number.`}
					</Text>
					<Text size="sm" className="mt-1">
						<a
							href={supportUrl}
							target="_blank"
							rel="noopener noreferrer"
							className="text-primary underline"
						>
							{t`Don't know your location number?`}
						</a>
					</Text>
					<div className="mt-4">
						<ButtonRow>
							<Button
								type="submit"
								size="lg"
								isDisabled={!locationNumber.trim()}
								className="w-full md:w-auto"
							>
								{t`Search`}
							</Button>
						</ButtonRow>
					</div>
				</form>
			</ContentCard>
		</CommonLayout>
	);
}
